import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/lib/seo";
import { offeringGroups } from "@/data/solutions";

const LEGACY_PATTERNS: Array<{
  test: RegExp;
  rewrite: (m: RegExpMatchArray) => string;
}> = [
  { test: /^\/(home|index)(\.html?)?\/?$/i, rewrite: () => "/" },
  { test: /^\/team\/?$/, rewrite: () => "/about" },
  { test: /^\/contact-us\/?$/, rewrite: () => "/contact" },
  { test: /^\/get-in-touch\/?$/, rewrite: () => "/contact" },
];

function matchLegacy(pathname: string): string | null {
  const cleaned = pathname.replace(/\/+$/, "") || "/";
  for (const { test, rewrite } of LEGACY_PATTERNS) {
    const m = cleaned.match(test);
    if (m) return rewrite(m);
  }
  return null;
}

export default function NotFound() {
  const [location, setLocation] = useLocation();
  const [redirecting, setRedirecting] = useState(false);

  useSeo({
    title: "Page Not Found | Relevant Dealer",
    description:
      "The page you are looking for could not be found. Browse our insights, services, or get in touch.",
    noIndex: true,
  });

  useEffect(() => {
    const target = matchLegacy(location);
    if (!target || target === location) return;
    setRedirecting(true);
    const meta = document.createElement("meta");
    meta.httpEquiv = "refresh";
    meta.content = `0; url=${target}`;
    meta.setAttribute("data-managed-redirect", "true");
    document.head.appendChild(meta);
    setLocation(target, { replace: true });
    return () => {
      document
        .querySelectorAll('meta[data-managed-redirect="true"]')
        .forEach((el) => el.remove());
    };
  }, [location, setLocation]);

  if (redirecting) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-stone-50">
        <p className="text-slate-600">Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-stone-50 px-6 pt-36 pb-16">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mx-auto text-center">
        <p className="text-sm font-semibold tracking-wider text-teal-600 uppercase mb-4">
          404
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
          We couldn't find that page.
        </h1>
        <p className="text-lg text-slate-600 mb-8">
          The URL you followed may have moved or never existed. Try one of
          these instead.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link href="/">
            <Button className="bg-teal-500 hover:bg-teal-600 text-white">
              Home
            </Button>
          </Link>
          <Link href="/insights">
            <Button variant="outline">Insights</Button>
          </Link>
          <Link href="/services">
            <Button variant="outline">Services</Button>
          </Link>
          <Link href="/contact">
            <Button variant="outline">Contact</Button>
          </Link>
        </div>
        </div>
        <section className="mt-14 border-t border-border pt-10" aria-labelledby="offerings-title">
          <h2 id="offerings-title" className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 text-center">Find the right support for your dealership.</h2>
          <p className="text-slate-600 text-center mb-8">Explore our products and services below.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {offeringGroups.map((group) => (
              <div key={group.id}>
                <h3 className="text-sm uppercase tracking-widest font-bold text-primary mb-4">{group.title}</h3>
                <ul className="space-y-3">
                  {group.links.map((link) => (
                    <li key={link.href}>
                      <Link href={link.name === "Dealership Websites" ? "/dealership-websites" : link.href} className="block rounded-xl border border-border bg-white px-5 py-4 font-semibold text-primary hover:bg-primary hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
